
import { useState, useEffect } from 'react';

interface Ambiente {
  id: number;
  imagen: string;
  alt: string;
}

const ambientes: Ambiente[] = [
  { id: 1, imagen: '/images/ambientes/opcion1.png', alt: 'Muebles de baño moderno con diseño contemporáneo' },
  { id: 2, imagen: '/images/ambientes/opcion2.png', alt: 'Muebles de baño de lujo con acabados premium' },
  { id: 3, imagen: '/images/ambientes/opcion3.png', alt: 'Muebles de baño minimalista con líneas limpias' },
  { id: 4, imagen: '/images/ambientes/opcion4.png', alt: 'Mueble de baño clásico con toques modernos' },
  { id: 5, imagen: '/images/ambientes/opcion5.png', alt: 'Muebles de baño funcional con maderas naturales' },
  { id: 6, imagen: '/images/ambientes/opcion6.png', alt: 'Muebles de baño con iluminación de diseño' },
  { id: 7, imagen: '/images/ambientes/opcion7.png', alt: 'Muebles de baño con acabados naturales personalizados' },
];

export default function AmbientesSection() {
  const [indiceCarrusel, setIndiceCarrusel] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isFading, setIsFading] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      siguienteAmbiente();
    }, 3000);

    return () => clearInterval(interval);
  }, [indiceCarrusel]);

  const siguienteAmbiente = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setIndiceCarrusel((prev) => (prev + 1) % ambientes.length);
    setTimeout(() => setIsTransitioning(false), 700);
  };

  const irASlide = (index: number) => {
    if (isTransitioning || index === indiceCarrusel) return;
    setIsTransitioning(true);
    setIsFading(true);
    setIndiceCarrusel(index);
    setTimeout(() => {
      setIsTransitioning(false);
      setIsFading(false);
    }, 700);
  };

  const obtenerIndiceVisible = (offset: number) => {
    return (indiceCarrusel + offset + ambientes.length) % ambientes.length;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-[#191a19]" style={{ padding: 'clamp(3rem, 5vh, 8rem) 0' }}>
        <div className="w-full" style={{ padding: '0 clamp(1rem, 3vw, 5rem)' }}>
          <h1 
            className="font-light text-white text-center" 
            style={{ 
              fontFamily: 'Playfair Display, serif',
              fontSize: 'clamp(2rem, 3.5vw, 5rem)',
              marginBottom: 'clamp(2rem, 4vh, 6rem)'
            }}
          >
            Muebles de baño
          </h1>

          <div className="relative w-full">
            {/* Desktop: 3 imágenes con separación y efectos visuales */}
            <div 
              className="hidden md:flex items-center justify-center" 
              style={{ 
                gap: 'clamp(1rem, 2vw, 3rem)',
                height: 'clamp(500px, 60vh, 800px)',
                padding: '0 clamp(1rem, 2vw, 3rem)'
              }}
            >
              {/* Imagen izquierda */}
              <div 
                className={`flex-shrink-0 transition-transform duration-1000 ease-in-out transform-gpu cursor-pointer ${isFading ? 'opacity-70 scale-95' : ''}`}
                style={{ perspective: 1200 }} 
                onClick={() => irASlide(obtenerIndiceVisible(-1))}
              >
                <img
                  src={ambientes[obtenerIndiceVisible(-1)].imagen}
                  alt={ambientes[obtenerIndiceVisible(-1)].alt}
                  className="object-cover rounded-xl shadow-2xl opacity-60 hover:opacity-80 transform-gpu"
                  style={{
                    width: 'clamp(200px, 18vw, 350px)',
                    height: 'clamp(350px, 45vh, 600px)',
                    transform: 'rotateY(12deg) translateZ(-20px) scale(0.92)',
                    filter: 'blur(1px) contrast(0.95)',
                    transition: 'all 0.7s ease'
                  }}
                />
              </div>

              {/* Imagen central */}
              <div 
                className={`flex-shrink-0 transition-all duration-1000 ease-out transform-gpu cursor-pointer ${isFading ? 'opacity-80 scale-98' : 'opacity-100 scale-100'}`} 
                onClick={() => irASlide(indiceCarrusel)}
              >
                <img
                  src={ambientes[indiceCarrusel].imagen}
                  alt={ambientes[indiceCarrusel].alt}
                  className="object-cover rounded-2xl transform-gpu hover:scale-102 transition-transform duration-700"
                  style={{
                    width: 'clamp(280px, 25vw, 500px)',
                    height: 'clamp(450px, 55vh, 750px)',
                    boxShadow: '0 30px 60px rgba(0,0,0,0.5)',
                    willChange: 'transform, box-shadow'
                  }}
                />
              </div>

              {/* Imagen derecha */}
              <div 
                className={`flex-shrink-0 transition-transform duration-1000 ease-in-out transform-gpu cursor-pointer ${isFading ? 'opacity-70 scale-95' : ''}`}
                style={{ perspective: 1200 }} 
                onClick={() => irASlide(obtenerIndiceVisible(1))}
              >
                <img
                  src={ambientes[obtenerIndiceVisible(1)].imagen}
                  alt={ambientes[obtenerIndiceVisible(1)].alt}
                  className="object-cover rounded-xl shadow-2xl opacity-60 hover:opacity-80 transform-gpu"
                  style={{
                    width: 'clamp(200px, 18vw, 350px)',
                    height: 'clamp(350px, 45vh, 600px)',
                    transform: 'rotateY(-12deg) translateZ(-20px) scale(0.92)',
                    filter: 'blur(1px) contrast(0.95)',
                    transition: 'all 0.7s ease'
                  }}
                />
              </div>
            </div>

            {/* Móvil: cross-fade slideshow */}
            <div 
              className="md:hidden relative flex items-center justify-center" 
              style={{ 
                height: 'clamp(500px, 70vh, 700px)',
                padding: '0 clamp(1rem, 4vw, 2rem)'
              }}
            >
              <div className="w-full max-w-sm relative overflow-hidden" style={{ height: 'clamp(400px, 55vh, 600px)' }}>
                {ambientes.map((amb, idx) => (
                  <img
                    key={amb.id}
                    src={amb.imagen}
                    alt={amb.alt}
                    className={`absolute inset-0 w-full h-full object-cover rounded-lg shadow-2xl transition-opacity duration-1000 ${idx === indiceCarrusel ? 'opacity-100 z-20' : 'opacity-0 z-10'}`}
                    style={{ transitionTimingFunction: 'cubic-bezier(.22,.9,.28,1)' }}
                  />
                ))}
              </div>
            </div>

            {/* Indicadores */}
            <div 
              className="flex justify-center items-center" 
              style={{ 
                marginTop: 'clamp(1.5rem, 2vh, 3rem)',
                gap: 'clamp(0.4rem, 0.5vw, 0.75rem)'
              }}
            >
              {ambientes.map((_, index) => (
                <button
                  key={index}
                  onClick={() => irASlide(index)}
                  disabled={isTransitioning}
                  className={`transition-all cursor-pointer disabled:cursor-not-allowed rounded-full ${
                    index === indiceCarrusel 
                      ? 'bg-white' 
                      : 'bg-white/30 hover:bg-white/50'
                  }`}
                  style={{
                    width: index === indiceCarrusel ? 'clamp(1.5rem, 2vw, 2.5rem)' : 'clamp(0.4rem, 0.5vw, 0.75rem)',
                    height: 'clamp(0.4rem, 0.5vw, 0.75rem)'
                  }}
                />
              ))}
            </div>

            {/* Contador */}
            <div className="text-center" style={{ marginTop: 'clamp(1rem, 1.5vh, 2rem)' }}>
              <span 
                className="text-white/60" 
                style={{ 
                  fontFamily: 'Cormorant Garamond, serif',
                  fontSize: 'clamp(0.875rem, 1vw, 1.125rem)'
                }}
              >
                {indiceCarrusel + 1} / {ambientes.length}
              </span>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
